import { useTranslation } from 'react-i18next';
import type { SavedProduct } from '@/mocks/dashboard';

interface Props {
  products: SavedProduct[];
}

export default function DashboardSavedProducts({ products }: Props) {
  const { t } = useTranslation('dashboard');

  return (
    <div className="rounded-xl border border-ink-100 bg-white">
      <div className="flex items-center justify-between px-4 md:px-5 py-3 md:py-4 border-b border-ink-100">
        <div className="flex items-center gap-2">
          <i className="ri-heart-line text-warm-400 text-lg" />
          <h3 className="text-sm md:text-base font-semibold text-ink-600">{t('dashboard.savedProducts.title')}</h3>
        </div>
        <a href="/my/favorites" className="text-xs md:text-sm font-medium text-warm-400 hover:text-warm-500 transition-colors whitespace-nowrap">
          {t('dashboard.seeAll')}
        </a>
      </div>

      {products.length === 0 ? (
        <div className="px-4 md:px-5 py-8 text-center">
          <div className="w-12 h-12 rounded-full bg-cream-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-heart-line text-cream-400 text-xl" />
          </div>
          <p className="text-sm text-ink-300">{t('dashboard.savedProducts.empty')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-4 md:p-5">
          {products.map((p) => (
            <div key={p.id} className="rounded-lg border border-ink-100 overflow-hidden hover:border-warm-300 transition-colors">
              <div className="w-full h-32 bg-cream-100 overflow-hidden">
                <img
                  src={p.image}
                  alt={p.productName}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              </div>
              <div className="p-3">
                <p className="text-sm font-medium text-ink-600 truncate">{p.productName}</p>
                <div className="flex items-center gap-2 mt-1 text-xs text-ink-300">
                  <span>{p.category}</span>
                  <span className="text-ink-200">•</span>
                  <span className="font-mono">{p.sku}</span>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <a
                    href={`/packaging/${p.id}`}
                    className="text-xs font-medium text-warm-400 hover:text-warm-500 transition-colors whitespace-nowrap"
                  >
                    {t('dashboard.savedProducts.view')}
                  </a>
                  <span className="text-ink-200">|</span>
                  <a
                    href={`/packaging/${p.id}?action=quote`}
                    className="text-xs font-medium text-warm-400 hover:text-warm-500 transition-colors whitespace-nowrap"
                  >
                    {t('dashboard.savedProducts.quote')}
                  </a>
                  <span className="text-ink-200">|</span>
                  <a
                    href={`/packaging/${p.id}?action=preview`}
                    className="text-xs font-medium text-warm-600 hover:text-warm-700 transition-colors whitespace-nowrap"
                  >
                    {t('dashboard.savedProducts.preview')}
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}